/**
 * NMS Video Carousel Component
 * Extends NMSBaseClass following NMS Component Instance Architecture
 * Handles video carousel with play/pause controls and single-video playback
 */

class NMSVideoCarousel extends window.NMSBaseClass {
  #isInitialized = false;
  #eventListeners = [];

  constructor(element) {
    super();
    this.element = element; // REQUIRED: Store element reference
    this.carouselId = element.id || `video-carousel-${Date.now()}`; // REQUIRED: Unique identifier

    // REQUIRED: Store in global registry for external access
    window.neemans = window.neemans || {};
    window.neemans.videoCarousel = window.neemans.videoCarousel || {};
    window.neemans.videoCarousel[this.carouselId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    super.init();
    this.#setupCarouselRefs();
    this.#initializeCarouselState();
    this.#bindCarouselEvents();
    this.#isInitialized = true;
  }

  // REQUIRED: Scope all setup to this.element
  #setupCarouselRefs() {
    // Query only within component scope
    this.refs.playButtons = this.element.querySelectorAll('[data-nms-js="video-play-pause"]');
    this.refs.videos = this.element.querySelectorAll('video');
    this.refs.videoItems = this.element.querySelectorAll('[data-nms-js="video-item"]');
  }

  #initializeCarouselState() {
    // Set up global data in window.neemans namespace
    this.setGlobalData(`videoCarouselData.${this.carouselId}`, {
      currentPlayingVideo: null,
      videosState: new Map(),
    });

    // Initialize video states
    const videosState = new Map();
    this.refs.videos.forEach((video, index) => {
      videosState.set(video, {
        isPlaying: false,
        index: index,
        button: this.#getButtonForVideo(video),
      });
    });
    
    this.setGlobalData(`videoCarouselData.${this.carouselId}.videosState`, videosState);
  }

  #bindCarouselEvents() {
    // Play/Pause button events
    this.refs.playButtons.forEach(button => {
      const playPauseHandler = (event) => this.#handlePlayPause(event);
      button.addEventListener('click', playPauseHandler);
      this.#eventListeners.push({ element: button, event: 'click', handler: playPauseHandler });
    });

    // Video ended events
    this.refs.videos.forEach(video => {
      const endedHandler = (event) => this.#handleVideoEnd(event);
      video.addEventListener('ended', endedHandler);
      this.#eventListeners.push({ element: video, event: 'ended', handler: endedHandler });
    });
  }

  #handlePlayPause(event) {
    const button = event.currentTarget;
    const video = button.closest('[data-nms-js="video-item"]')?.querySelector('video');
    
    if (!video) {
      this.handleError(new Error('Video not found'), 'Video element missing for play/pause button');
      return;
    }

    try {
      if (video.paused) {
        this.#pauseAllVideos(video);
        video.play();
        this.#updateVideoState(video, true);
        this.#updateButtonState(button, true);
      } else {
        video.pause();
        this.#updateVideoState(video, false);
        this.#updateButtonState(button, false);
      }
    } catch (error) {
      this.handleError(error, 'Failed to play/pause video');
    }
  }

  #handleVideoEnd(event) {
    const video = event.target;
    const button = this.#getButtonForVideo(video);
    
    if (button) {
      this.#updateVideoState(video, false);
      this.#updateButtonState(button, false);
    }

    // Clear current playing video
    this.setGlobalData(`videoCarouselData.${this.carouselId}.currentPlayingVideo`, null);
  }

  #updateButtonState(button, isPlaying) {
    const playIcon = button.querySelector('[data-nms-js="video-play-icon"]');
    const pauseIcon = button.querySelector('[data-nms-js="video-pause-icon"]');
    
    if (playIcon && pauseIcon) {
      playIcon.style.display = isPlaying ? 'none' : 'block';
      pauseIcon.style.display = isPlaying ? 'block' : 'none';
      button.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    }
  }

  #updateVideoState(video, isPlaying) {
    const videosState = this.getGlobalData(`videoCarouselData.${this.carouselId}.videosState`);
    if (videosState && videosState.has(video)) {
      const state = videosState.get(video);
      state.isPlaying = isPlaying;
      videosState.set(video, state);
      this.setGlobalData(`videoCarouselData.${this.carouselId}.videosState`, videosState);
    }

    // Update current playing video
    this.setGlobalData(
      `videoCarouselData.${this.carouselId}.currentPlayingVideo`, 
      isPlaying ? video : null
    );
  }

  #pauseAllVideos(currentVideo) {
    this.refs.videos.forEach(video => {
      if (video !== currentVideo && !video.paused) {
        try {
          video.pause();
          const button = this.#getButtonForVideo(video);
          if (button) {
            this.#updateVideoState(video, false);
            this.#updateButtonState(button, false);
          }
        } catch (error) {
          this.handleError(error, 'Failed to pause video');
        }
      }
    });
  }

  #getButtonForVideo(video) {
    return video.closest('[data-nms-js="video-item"]')?.querySelector('[data-nms-js="video-play-pause"]');
  }

  // Public API methods
  playVideo(videoIndex) {
    if (videoIndex >= 0 && videoIndex < this.refs.videos.length) {
      const video = this.refs.videos[videoIndex];
      const button = this.#getButtonForVideo(video);
      if (video && button) {
        this.#pauseAllVideos(video);
        video.play();
        this.#updateVideoState(video, true);
        this.#updateButtonState(button, true);
      }
    }
  }

  pauseVideo(videoIndex) {
    if (videoIndex >= 0 && videoIndex < this.refs.videos.length) {
      const video = this.refs.videos[videoIndex];
      const button = this.#getButtonForVideo(video);
      if (video && button) {
        video.pause();
        this.#updateVideoState(video, false);
        this.#updateButtonState(button, false);
      }
    }
  }

  pauseAllVideos() {
    this.#pauseAllVideos();
  }

  getCurrentPlayingVideo() {
    return this.getGlobalData(`videoCarouselData.${this.carouselId}.currentPlayingVideo`);
  }

  getVideoState(videoIndex) {
    if (videoIndex >= 0 && videoIndex < this.refs.videos.length) {
      const video = this.refs.videos[videoIndex];
      const videosState = this.getGlobalData(`videoCarouselData.${this.carouselId}.videosState`);
      return videosState?.get(video) || null;
    }
    return null;
  }

  destroy() {
    // Clean up event listeners
    for (const listener of this.#eventListeners) {
      listener.element.removeEventListener(listener.event, listener.handler);
    }
    this.#eventListeners = [];

    // Pause all videos before cleanup
    this.refs.videos.forEach(video => {
      if (!video.paused) {
        video.pause();
      }
    });

    // Clean up global reference
    if (window.neemans.videoCarousel?.[this.carouselId]) {
      delete window.neemans.videoCarousel[this.carouselId];
    }

    super.destroy();
    this.#isInitialized = false;
  }
}

// REQUIRED: Standard initialization pattern
function initNMSVideoCarousel() {
  if (!window.NMSBaseClass) {
    console.error('NMSVideoCarousel: NMSBaseClass required');
    return;
  }

  // Find all video carousel components
  const elements = document.querySelectorAll('[data-nms-js="video-carousel"]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSVideoCarousel(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSVideoCarousel);
} else {
  initNMSVideoCarousel();
}

// Handle theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-js="video-carousel"]')) {
    initNMSVideoCarousel();
  }
});

// Make available in global namespace
window.neemans = window.neemans || {};
window.neemans.NMSVideoCarousel = NMSVideoCarousel;

// Global initialization function for other components to use
window.initNMSVideoCarousel = initNMSVideoCarousel;